/**
 * ai-header.js — Header Web Component (V0)
 *
 * Handles:
 * - Transparent / solid state via IntersectionObserver
 * - Sticky / hide-on-scroll behavior
 * - Desktop dropdown (hover/focus/click)
 * - Mobile drawer with drill-down subpanels
 * - Locale selector (open/close/select/submit)
 * - Escape priority (locale > subpanel > drawer)
 * - Focus trap in drawer
 * - Scroll lock via html.aih-scroll-locked
 * - --header-height CSS variable
 * - Mouse proximity anti-hide (desktop)
 * - body[header-transparent] contract
 */

/* ========================================================================
   HeaderStateManager — pure logic, testable without DOM
   ======================================================================== */

class HeaderStateManager {
  constructor() {
    this._scrollLockCount = 0;
    this._lastScrollY = 0;
    this._scrollDirection = "up";
  }

  updateScroll(scrollY) {
    if (scrollY > this._lastScrollY + 2) {
      this._scrollDirection = "down";
    } else if (scrollY < this._lastScrollY - 2) {
      this._scrollDirection = "up";
    }
    this._lastScrollY = scrollY;
  }

  get scrollDirection() {
    return this._scrollDirection;
  }

  get lastScrollY() {
    return this._lastScrollY;
  }

  shouldHide(stickyType, { mouseY, pointerFine, hasOpenDropdown, hasOpenLocale, hasOpenDrawerItem, drawerOpen }) {
    if (stickyType === "none") return false;
    if (stickyType === "always") return false;
    if (drawerOpen) return false;
    if (hasOpenDropdown) return false;
    if (hasOpenLocale) return false;
    if (hasOpenDrawerItem) return false;
    if (pointerFine && mouseY >= 0 && mouseY < 100) return false;
    return this._scrollDirection === "down";
  }

  resolveEscapeTarget(context) {
    // context: { openLocales: Element[], openDrawerItems: Element[], drawerOpen: boolean }
    if (context.openLocales.length > 0) {
      return { action: "close-locale", target: context.openLocales[context.openLocales.length - 1] };
    }
    if (context.openDrawerItems.length > 0) {
      return { action: "close-subpanel", target: context.openDrawerItems[context.openDrawerItems.length - 1] };
    }
    if (context.drawerOpen) {
      return { action: "close-drawer" };
    }
    return { action: "none" };
  }

  lockScroll() {
    this._scrollLockCount++;
    return this._scrollLockCount === 1;
  }

  unlockScroll() {
    if (this._scrollLockCount > 0) this._scrollLockCount--;
    return this._scrollLockCount === 0;
  }

  get scrollLockCount() {
    return this._scrollLockCount;
  }

  shouldBeExclusive(type, currentOpen) {
    // type: "dropdown" | "locale"
    // Returns arrays of elements to close
    const result = { closeDropdowns: [], closeLocales: [] };
    if (type === "dropdown") {
      result.closeLocales = currentOpen.locales || [];
    } else if (type === "locale") {
      result.closeDropdowns = currentOpen.dropdowns || [];
      result.closeLocales = (currentOpen.locales || []).filter(el => el !== currentOpen.opening);
    }
    return result;
  }
}

/* ========================================================================
   ai-header Web Component
   ======================================================================== */

if (!customElements.get("ai-header")) {
  customElements.define(
    "ai-header",
    class AiHeader extends HTMLElement {
      constructor() {
        super();
        this._state = new HeaderStateManager();
        this._mouseY = -1;
        this._wroteBodyTransparent = false;
        this._observer = null;
        this._resizeObserver = null;
        this._isAtTop = true;
        this._focusTrapActive = false;
        this._boundHandlers = {};
        this._elementListeners = [];
        this._suppressFocusOpen = false;
        this._cartUpdateUnsubscriber = null;
      }

      connectedCallback() {
        this._cacheElements();
        this._bindEvents();
        this._bindCartEvents();
        this._initObservers();
        this._initTransparent();
        this._measureHeight();
      }

      disconnectedCallback() {
        this._unbindEvents();
        this._cartUpdateUnsubscriber?.();
        this._cartUpdateUnsubscriber = null;
        this._destroyObservers();
        if (this._wroteBodyTransparent) {
          document.body.removeAttribute("header-transparent");
          this._wroteBodyTransparent = false;
        }
        if (this._state.scrollLockCount > 0) {
          document.documentElement.classList.remove("aih-scroll-locked");
        }
      }

      /* ------ Element cache ------ */

      _cacheElements() {
        const root = this;
        this._bar = root.querySelector(".aih-bar");
        this._drawer = root.querySelector(".aih-drawer");
        this._drawerTrigger = root.querySelector(".aih-drawer-trigger");
        this._drawerClose = root.querySelector(".aih-drawer-close");
        this._drawerOverlay = root.querySelector(".aih-drawer-overlay");
        this._drawerPanel = root.querySelector(".aih-drawer-panel");
        this._scrollTracker = this.previousElementSibling;
        if (!this._scrollTracker || !this._scrollTracker.classList.contains("aih-scroll-tracker")) {
          this._scrollTracker = this.parentElement?.querySelector(".aih-scroll-tracker");
        }
      }

      /* ------ Event binding ------ */

      _on(el, event, handler, opts) {
        el.addEventListener(event, handler, opts);
        this._elementListeners.push({ el, event, handler, opts });
      }

      _bindEvents() {
        const h = this._boundHandlers;

        h.scroll = () => this._onScroll();
        h.keydown = (e) => this._onKeydown(e);
        h.mousemove = (e) => { this._mouseY = e.clientY; };
        h.click = (e) => this._onDocumentClick(e);

        window.addEventListener("scroll", h.scroll, { passive: true });
        document.addEventListener("keydown", h.keydown);
        document.addEventListener("mousemove", h.mousemove, { passive: true });
        document.addEventListener("click", h.click);

        // Desktop dropdown hover/focus
        this.querySelectorAll(".aih-has-dropdown").forEach(li => {
          this._on(li, "mouseenter", () => this._openDropdown(li));
          this._on(li, "mouseleave", () => this._closeDropdown(li));
          this._on(li, "focusin", () => { if (!this._suppressFocusOpen) this._openDropdown(li); });
          this._on(li, "focusout", () => {
            requestAnimationFrame(() => {
              if (!li.contains(document.activeElement)) this._closeDropdown(li);
            });
          });
        });

        // Desktop dropdown trigger click (keyboard/a11y fallback)
        this.querySelectorAll(".aih-dropdown-trigger").forEach(btn => {
          this._on(btn, "click", (e) => {
            e.preventDefault();
            const li = btn.closest(".aih-has-dropdown");
            if (li.getAttribute("data-open") === "true") {
              this._closeDropdown(li);
            } else {
              this._openDropdown(li);
            }
          });
        });

        // Locale triggers
        this.querySelectorAll(".aih-locale-trigger").forEach(trigger => {
          this._on(trigger, "click", (e) => {
            e.preventDefault();
            const locale = trigger.closest(".aih-locale");
            if (locale.getAttribute("data-open") === "true") {
              this._closeLocale(locale);
            } else {
              this._openLocale(locale);
            }
          });
        });

        // Locale options
        this.querySelectorAll(".aih-locale-option").forEach(option => {
          this._on(option, "click", (e) => {
            e.preventDefault();
            this._selectLocaleOption(option);
          });
        });

        // Drawer trigger
        if (this._drawerTrigger) {
          this._on(this._drawerTrigger, "click", () => this._openDrawer());
        }

        // Drawer close + overlay
        if (this._drawerClose) {
          this._on(this._drawerClose, "click", () => this._closeDrawer());
        }
        if (this._drawerOverlay) {
          this._on(this._drawerOverlay, "click", () => this._closeDrawer());
        }

        // Drawer submenu triggers
        this.querySelectorAll(".aih-drawer-submenu-trigger").forEach(trigger => {
          this._on(trigger, "click", () => {
            const item = trigger.closest(".aih-drawer-item");
            this._openSubpanel(item);
          });
        });

        // Subpanel back buttons
        this.querySelectorAll(".aih-subpanel-back").forEach(btn => {
          this._on(btn, "click", () => {
            const subpanel = btn.closest(".aih-subpanel");
            const item = subpanel?.parentElement;
            if (item) this._closeSubpanel(item);
          });
        });
      }

      _unbindEvents() {
        const h = this._boundHandlers;
        window.removeEventListener("scroll", h.scroll);
        document.removeEventListener("keydown", h.keydown);
        document.removeEventListener("mousemove", h.mousemove);
        document.removeEventListener("click", h.click);

        for (const { el, event, handler, opts } of this._elementListeners) {
          el.removeEventListener(event, handler, opts);
        }
        this._elementListeners = [];
      }

      _bindCartEvents() {
        if (this._cartUpdateUnsubscriber || !window.AIThemeEvents?.subscribeCartUpdate) return;
        this._cartUpdateUnsubscriber = window.AIThemeEvents.subscribeCartUpdate(detail => {
          this._onCartUpdate(detail);
        });
      }

      _onCartUpdate(detail) {
        const cartData = detail?.cartData || detail;
        if (this._replaceCartBubbleFromSection(cartData)) return;
        const count = Number(cartData?.item_count);
        if (Number.isFinite(count)) {
          this._renderCartBubbleCount(count);
          return;
        }
        this._refreshCartBubbleCount();
      }

      _replaceCartBubbleFromSection(cartData) {
        const html = cartData?.sections?.["ai-cart-icon-bubble"];
        if (!html) return false;
        const source = new DOMParser()
          .parseFromString(html, "text/html")
          .querySelector(".ai-cart-bubble-anchor");
        const target = this.querySelector(".ai-cart-bubble-anchor");
        if (!source || !target) return false;
        target.innerHTML = source.innerHTML;
        return true;
      }

      _renderCartBubbleCount(count) {
        const target = this.querySelector(".ai-cart-bubble-anchor");
        if (!target) return;
        const value = Math.max(0, Number(count) || 0);
        target.innerHTML = value > 0
          ? `<span class="ai-cart-bubble absolute"><span aria-hidden="true">${value < 100 ? value : "99+"}</span></span>`
          : "";
      }

      async _refreshCartBubbleCount() {
        try {
          const response = await fetch(`${window.routes?.cart_url || "/cart"}.js`, {
            headers: { Accept: "application/json" },
          });
          if (!response.ok) return;
          const cart = await response.json();
          this._renderCartBubbleCount(cart.item_count);
        } catch {
          // Best-effort fallback when the cart update payload has no total count.
        }
      }

      /* ------ Observers ------ */

      _initObservers() {
        if (this._scrollTracker) {
          this._observer = new IntersectionObserver(
            ([entry]) => {
              this._isAtTop = entry.isIntersecting;
              this._updateSolidState();
            },
            { threshold: 0 }
          );
          this._observer.observe(this._scrollTracker);
        }

        this._resizeObserver = new ResizeObserver(() => this._measureHeight());
        this._resizeObserver.observe(this);
      }

      _destroyObservers() {
        if (this._observer) {
          this._observer.disconnect();
          this._observer = null;
        }
        if (this._resizeObserver) {
          this._resizeObserver.disconnect();
          this._resizeObserver = null;
        }
      }

      /* ------ Transparent / Solid ------ */

      _initTransparent() {
        if (this.getAttribute("data-transparent") === "true") {
          document.body.setAttribute("header-transparent", "");
          this._wroteBodyTransparent = true;
        }
        this._updateSolidState();
      }

      _updateSolidState() {
        const isTransparent = this.getAttribute("data-transparent") === "true";
        if (!isTransparent) {
          this.classList.remove("aih-solid");
          return;
        }
        if (this._isAtTop && !this.classList.contains("aih-drawer-open")) {
          this.classList.remove("aih-solid");
        } else {
          this.classList.add("aih-solid");
        }
      }

      /* ------ Scroll / Sticky ------ */

      _onScroll() {
        const scrollY = window.scrollY;
        this._state.updateScroll(scrollY);
        const stickyType = this.getAttribute("data-sticky-type") || "none";

        if (stickyType === "none") {
          this.classList.remove("aih-sticky", "aih-hidden", "aih-animate");
          return;
        }

        const pastThreshold = scrollY > 0;

        if (pastThreshold) {
          if (!this.classList.contains("aih-sticky")) {
            this.classList.add("aih-sticky");
            requestAnimationFrame(() => this.classList.add("aih-animate"));
          }
        } else {
          this.classList.remove("aih-sticky", "aih-hidden", "aih-animate");
          this._updateSolidState();
          return;
        }

        const pointerFine = window.matchMedia("(pointer: fine)").matches;
        const shouldHide = this._state.shouldHide(stickyType, {
          mouseY: this._mouseY,
          pointerFine,
          hasOpenDropdown: !!this.querySelector(".aih-has-dropdown[data-open='true']"),
          hasOpenLocale: !!this.querySelector(".aih-locale[data-open='true']"),
          hasOpenDrawerItem: !!this.querySelector(".aih-drawer-item[data-open='true']"),
          drawerOpen: this.classList.contains("aih-drawer-open"),
        });

        if (shouldHide) {
          this.classList.add("aih-hidden");
        } else {
          this.classList.remove("aih-hidden");
        }

        this._updateSolidState();
      }

      /* ------ Height measurement ------ */

      _measureHeight() {
        const height = this._bar ? this._bar.offsetHeight : this.offsetHeight;
        document.documentElement.style.setProperty("--header-height", height + "px");
      }

      /* ------ Desktop Dropdown ------ */

      _openDropdown(li) {
        // Close all other dropdowns and locales first
        const exclusions = this._state.shouldBeExclusive("dropdown", {
          locales: Array.from(this.querySelectorAll(".aih-locale[data-open='true']")),
        });
        exclusions.closeLocales.forEach(el => this._closeLocale(el));

        this.querySelectorAll(".aih-has-dropdown[data-open='true']").forEach(other => {
          if (other !== li) this._closeDropdown(other);
        });

        li.setAttribute("data-open", "true");
        const trigger = li.querySelector(".aih-dropdown-trigger");
        const panel = li.querySelector(".aih-dropdown-panel");
        if (trigger) trigger.setAttribute("aria-expanded", "true");
        if (panel) panel.removeAttribute("hidden");
      }

      _closeDropdown(li) {
        li.setAttribute("data-open", "false");
        const trigger = li.querySelector(".aih-dropdown-trigger");
        const panel = li.querySelector(".aih-dropdown-panel");
        if (trigger) trigger.setAttribute("aria-expanded", "false");
        if (panel) panel.setAttribute("hidden", "");
      }

      /* ------ Locale selector ------ */

      _openLocale(locale) {
        const exclusions = this._state.shouldBeExclusive("locale", {
          dropdowns: Array.from(this.querySelectorAll(".aih-has-dropdown[data-open='true']")),
          locales: Array.from(this.querySelectorAll(".aih-locale[data-open='true']")),
          opening: locale,
        });
        exclusions.closeDropdowns.forEach(el => this._closeDropdown(el));
        exclusions.closeLocales.forEach(el => this._closeLocale(el));

        locale.setAttribute("data-open", "true");
        const trigger = locale.querySelector(".aih-locale-trigger");
        const panel = locale.querySelector(".aih-locale-panel");
        if (trigger) trigger.setAttribute("aria-expanded", "true");
        if (panel) {
          clearTimeout(panel._hideTimer);
          if (panel._onTransitionEnd) panel.removeEventListener("transitionend", panel._onTransitionEnd);
          panel._onTransitionEnd = null;
          panel.removeAttribute("hidden");
        }
      }

      _closeLocale(locale) {
        locale.setAttribute("data-open", "false");
        const trigger = locale.querySelector(".aih-locale-trigger");
        const panel = locale.querySelector(".aih-locale-panel");
        if (trigger) trigger.setAttribute("aria-expanded", "false");
        if (panel) {
          clearTimeout(panel._hideTimer);
          if (panel._onTransitionEnd) panel.removeEventListener("transitionend", panel._onTransitionEnd);
          panel._onTransitionEnd = () => {
            if (locale.getAttribute("data-open") !== "true") panel.setAttribute("hidden", "");
            panel.removeEventListener("transitionend", panel._onTransitionEnd);
            panel._onTransitionEnd = null;
          };
          panel.addEventListener("transitionend", panel._onTransitionEnd);
          panel._hideTimer = setTimeout(() => {
            if (locale.getAttribute("data-open") !== "true" && !panel.hasAttribute("hidden")) {
              panel.setAttribute("hidden", "");
            }
          }, 350);
        }
      }

      _selectLocaleOption(option) {
        const value = option.getAttribute("data-value");
        const locale = option.closest(".aih-locale");
        if (!locale) return;

        const submitInput = locale.querySelector(".aih-locale-submit-input")
          || locale.parentElement?.querySelector(".aih-locale-submit-input");

        const form = submitInput?.closest("form");
        if (submitInput) submitInput.value = value;
        this._closeLocale(locale);
        if (form) form.submit();
      }

      /* ------ Mobile Drawer ------ */

      _openDrawer() {
        if (!this._drawer) return;
        clearTimeout(this._drawer._hideTimer);
        if (this._drawer._onTransitionEnd) this._drawer.removeEventListener("transitionend", this._drawer._onTransitionEnd);
        this._drawer._onTransitionEnd = null;
        this._drawer.removeAttribute("hidden");
        this.classList.add("aih-drawer-open", "aih-solid");
        if (this._drawerTrigger) this._drawerTrigger.setAttribute("aria-expanded", "true");

        if (this._state.lockScroll()) {
          document.documentElement.classList.add("aih-scroll-locked");
        }

        requestAnimationFrame(() => {
          this._drawer.classList.add("aih-drawer-active");
          const firstFocusable = this._drawerClose || this._drawerPanel?.querySelector("a, button");
          if (firstFocusable) firstFocusable.focus();
          this._enableFocusTrap();
        });
      }

      _closeDrawer() {
        if (!this._drawer) return;

        // Close all open locale panels and subpanels within drawer
        this._drawer.querySelectorAll(".aih-locale[data-open='true']").forEach(el => {
          el.setAttribute("data-open", "false");
          const t = el.querySelector(".aih-locale-trigger");
          const p = el.querySelector(".aih-locale-panel");
          if (t) t.setAttribute("aria-expanded", "false");
          if (p) p.setAttribute("hidden", "");
        });
        this._drawer.querySelectorAll(".aih-drawer-item[data-open='true']").forEach(item => {
          item.setAttribute("data-open", "false");
          const t = this._getDrawerSubmenuTrigger(item);
          const p = item.querySelector(":scope > .aih-subpanel");
          if (t) t.setAttribute("aria-expanded", "false");
          if (p) p.setAttribute("hidden", "");
        });

        this._drawer.classList.remove("aih-drawer-active");
        this.classList.remove("aih-drawer-open");
        if (this._drawerTrigger) this._drawerTrigger.setAttribute("aria-expanded", "false");

        if (this._state.unlockScroll()) {
          document.documentElement.classList.remove("aih-scroll-locked");
        }

        this._disableFocusTrap();
        this._updateSolidState();

        if (this._drawer._onTransitionEnd) this._drawer.removeEventListener("transitionend", this._drawer._onTransitionEnd);
        this._drawer._onTransitionEnd = () => {
          if (!this.classList.contains("aih-drawer-open")) this._drawer.setAttribute("hidden", "");
          this._drawer.removeEventListener("transitionend", this._drawer._onTransitionEnd);
          this._drawer._onTransitionEnd = null;
        };
        this._drawer.addEventListener("transitionend", this._drawer._onTransitionEnd);
        this._drawer._hideTimer = setTimeout(() => {
          if (!this.classList.contains("aih-drawer-open") && !this._drawer.hasAttribute("hidden")) {
            this._drawer.setAttribute("hidden", "");
          }
        }, 500);

        if (this._drawerTrigger) this._drawerTrigger.focus();
      }

      /* ------ Subpanel drill-down ------ */

      _getDrawerSubmenuTrigger(item) {
        if (!item) return null;
        return item.querySelector(":scope > .aih-drawer-item-row > .aih-drawer-submenu-trigger")
          || item.querySelector(":scope > .aih-drawer-submenu-trigger");
      }

      _openSubpanel(item) {
        if (!item) return;
        item.setAttribute("data-open", "true");
        const trigger = this._getDrawerSubmenuTrigger(item);
        const panel = item.querySelector(":scope > .aih-subpanel");
        if (trigger) trigger.setAttribute("aria-expanded", "true");
        if (panel) {
          clearTimeout(panel._hideTimer);
          if (panel._onTransitionEnd) panel.removeEventListener("transitionend", panel._onTransitionEnd);
          panel._onTransitionEnd = null;
          panel.removeAttribute("hidden");
        }
      }

      _closeSubpanel(item) {
        if (!item) return;
        item.setAttribute("data-open", "false");
        const trigger = this._getDrawerSubmenuTrigger(item);
        const panel = item.querySelector(":scope > .aih-subpanel");
        if (trigger) trigger.setAttribute("aria-expanded", "false");
        if (panel) {
          clearTimeout(panel._hideTimer);
          if (panel._onTransitionEnd) panel.removeEventListener("transitionend", panel._onTransitionEnd);
          panel._onTransitionEnd = () => {
            if (item.getAttribute("data-open") !== "true") panel.setAttribute("hidden", "");
            panel.removeEventListener("transitionend", panel._onTransitionEnd);
            panel._onTransitionEnd = null;
          };
          panel.addEventListener("transitionend", panel._onTransitionEnd);
          panel._hideTimer = setTimeout(() => {
            if (item.getAttribute("data-open") !== "true" && !panel.hasAttribute("hidden")) {
              panel.setAttribute("hidden", "");
            }
          }, 350);
        }
        if (trigger) trigger.focus();
      }

      /* ------ Keyboard ------ */

      _onKeydown(e) {
        if (e.key !== "Escape") return;

        const drawerOpen = this.classList.contains("aih-drawer-open");

        if (drawerOpen && this._drawer) {
          const openLocales = Array.from(this._drawer.querySelectorAll(".aih-locale[data-open='true']"));
          const openItems = Array.from(this._drawer.querySelectorAll(".aih-drawer-item[data-open='true']"));

          const resolved = this._state.resolveEscapeTarget({
            openLocales,
            openDrawerItems: openItems,
            drawerOpen,
          });

          if (resolved.action === "close-locale") {
            e.preventDefault();
            e.stopPropagation();
            this._closeLocale(resolved.target);
            const trigger = resolved.target.querySelector(".aih-locale-trigger");
            if (trigger) trigger.focus();
            return;
          }

          if (resolved.action === "close-subpanel") {
            e.preventDefault();
            e.stopPropagation();
            this._closeSubpanel(resolved.target);
            return;
          }

          if (resolved.action === "close-drawer") {
            e.preventDefault();
            e.stopPropagation();
            this._closeDrawer();
            return;
          }
        }

        // Not in drawer — check desktop locale panels and dropdowns
        const openLocale = this.querySelector(".aih-locale[data-open='true']");
        if (openLocale) {
          e.preventDefault();
          this._closeLocale(openLocale);
          const trigger = openLocale.querySelector(".aih-locale-trigger");
          if (trigger) trigger.focus();
          return;
        }

        const openDropdown = this.querySelector(".aih-has-dropdown[data-open='true']");
        if (openDropdown) {
          e.preventDefault();
          this._closeDropdown(openDropdown);
          this._suppressFocusOpen = true;
          const trigger = openDropdown.querySelector(".aih-dropdown-trigger");
          if (trigger) trigger.focus();
          requestAnimationFrame(() => { this._suppressFocusOpen = false; });
        }
      }

      /* ------ Document click (outside close) ------ */

      _onDocumentClick(e) {
        // Close locale panels on outside click
        this.querySelectorAll(".aih-locale[data-open='true']").forEach(locale => {
          if (!locale.contains(e.target)) {
            this._closeLocale(locale);
          }
        });
      }

      /* ------ Focus trap ------ */

      _enableFocusTrap() {
        if (!this._drawer) return;
        this._focusTrapActive = true;
        this._boundHandlers.focusTrap = (e) => this._trapFocus(e);
        this._drawer.addEventListener("keydown", this._boundHandlers.focusTrap);
      }

      _disableFocusTrap() {
        this._focusTrapActive = false;
        if (this._drawer && this._boundHandlers.focusTrap) {
          this._drawer.removeEventListener("keydown", this._boundHandlers.focusTrap);
        }
      }

      _trapFocus(e) {
        if (e.key !== "Tab" || !this._focusTrapActive) return;

        const focusable = this._drawer.querySelectorAll(
          'a[href]:not([hidden]):not([tabindex="-1"]), button:not([disabled]):not([hidden]):not([tabindex="-1"]), input:not([hidden]):not([tabindex="-1"])'
        );
        // Filter out elements inside hidden panels
        const visible = Array.from(focusable).filter(el => {
          const hiddenParent = el.closest("[hidden]");
          return !hiddenParent;
        });

        if (visible.length === 0) return;

        const first = visible[0];
        const last = visible[visible.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    }
  );
}
